import { defineStore } from 'pinia';
import api from '../services/api.js';

export const useAuthStore = defineStore('auth', {
  state: () => ({
    user: null,
    token: null,
    loading: false,
    error: null,
  }),

  getters: {
    isAuthenticated: (state) => !!state.token && !!state.user,
    isStudent: (state) => state.user?.role === 'student',
    isTeacher: (state) => state.user?.role === 'teacher',
    isTeamLeader: (state) => state.user?.role === 'teamleader',
    isAdministrator: (state) => state.user?.role === 'administrator',
  },

  actions: {
    /**
     * Initialize auth store from localStorage
     */
    init() {
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('auth_user');

      if (token && userStr) {
        try {
          this.token = token;
          this.user = JSON.parse(userStr);
        } catch (error) {
          console.error('Error parsing stored user data:', error);
          this.clearAuth();
        }
      }
    },

    /**
     * Login user
     */
    async login(email, password) {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.post('/auth/login', {
          email,
          password,
        });

        // Log response for debugging

        // Handle different response structures
        let user, token;
        if (response.data.data) {
          // Expected structure: { data: { user, token } }
          user = response.data.data.user;
          token = response.data.data.token;
        } else if (response.data.user && response.data.token) {
          // Alternative structure: { user, token }
          user = response.data.user;
          token = response.data.token;
        } else {
          throw new Error('Invalid response structure from server');
        }

        if (!user || !token) {
          throw new Error('Missing user or token in response');
        }

        this.user = user;
        this.token = token;

        // Store in localStorage
        localStorage.setItem('auth_token', token);
        localStorage.setItem('auth_user', JSON.stringify(user));

        return { user, token };
      } catch (error) {
        // Extract error message from response
        let errorMessage = 'Login failed';
        
        // Log full error for debugging FIRST, before any processing
        console.error('Login error details:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message,
          error: error
        });
        
        if (error.response?.data?.error) {
          // Backend error response structure: { error: { message: "...", ... } }
          if (typeof error.response.data.error === 'string') {
            errorMessage = error.response.data.error;
          } else if (error.response.data.error.message) {
            errorMessage = error.response.data.error.message;
          } else {
            errorMessage = JSON.stringify(error.response.data.error);
          }
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        // Set error message
        this.error = errorMessage;
        
        // Re-throw error so it can be caught by the component
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Logout user
     */
    async logout() {
      this.loading = true;

      try {
        // Call logout endpoint if authenticated
        if (this.token) {
          await api.post('/auth/logout');
        }
      } catch (error) {
        console.error('Logout error:', error);
        // Continue with logout even if API call fails
      } finally {
        this.clearAuth();
        this.loading = false;
      }
    },

    /**
     * Get current user info
     */
    async fetchMe() {
      this.loading = true;
      this.error = null;

      try {
        const response = await api.get('/auth/me');
        this.user = response.data.data;
        localStorage.setItem('auth_user', JSON.stringify(this.user));
        return this.user;
      } catch (error) {
        // Only set error for authentication errors (401), not for timeouts or network errors
        if (error.response?.status === 401) {
          this.error = error.response?.data?.error?.message || error.response?.data?.error || 'Authentication failed';
          // Clear auth data on 401
          this.clearAuth();
        } else {
          // For timeouts, network errors, etc., just log but don't set error
          // The calling code can decide what to do
          console.warn('Failed to fetch user info (non-auth error):', error.message);
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Clear authentication data
     */
    clearAuth() {
      this.user = null;
      this.token = null;
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
    },
  },
});









